import React, { useEffect, useState } from 'react';
import { Button, ProgressBar, Text } from 'src/@vymo/ui/atoms';
import { ImageLoader, List } from 'src/@vymo/ui/blocks';
import Modal, { Body, Header } from 'src/@vymo/ui/blocks/modal';
import { ReactComponent as NoDiffFound } from 'src/assets/images/noDiff.svg';
import useContainerSize from 'src/hooks/useContainerSize';
import { useAppDispatch } from 'src/store/hooks';
import { updateTestStatus } from '../thunks';
import { TestCase } from '../types';
import styles from './index.module.scss';

interface ImagePreviewModalProps {
  test: TestCase;
  prId: string;
  onClose: () => void;
}

function ImagePreviewBlock({
  test,
  getDataUrl,
}: {
  test: TestCase;
  getDataUrl: (fileName: string | null) => string;
}) {
  return (
    <>
      <div className={styles.testList__item__image}>
        <Text type="sublabel">Current</Text>
        {test.comparisonPath ? (
          <ImageLoader
            src={getDataUrl(test.comparisonPath)}
            alt="Current version"
            className={styles.testList__item__image__preview}
          />
        ) : (
          <ImageLoader
            src={getDataUrl(test.baselinePath)}
            alt="Baseline version"
            className={styles.testList__item__image__preview}
          />
        )}
      </div>

      <div className={styles.testList__item__image}>
        <Text type="sublabel">Diff</Text>
        {test?.diffPath ? (
          <ImageLoader
            src={getDataUrl(test?.diffPath)}
            alt="Diff version"
            className={styles.testList__item__image__preview}
          />
        ) : (
          <div className={styles.testList__item__image__noDiff}>
            <NoDiffFound />
          </div>
        )}
      </div>

      <div className={styles.testList__item__image}>
        <Text type="sublabel">Baseline</Text>
        {test.comparisonPath ? (
          <ImageLoader
            src={getDataUrl(test.baselinePath)}
            alt="Baseline version"
            className={styles.testList__item__image__preview}
          />
        ) : (
          <div className={styles.testList__item__image__noDiff} />
        )}
      </div>
    </>
  );
}

function ImagePreviewModal({ test, onClose, prId }: ImagePreviewModalProps) {
  const getDataUrl = (fileName: string | null) =>
    `/frontend/api/visualTests/${prId}/image?pathname=${fileName}`;

  return (
    <Modal onClose={onClose} classNames={styles.testList__modal}>
      <Header>
        <Text type="h6">{test.name}</Text>
      </Header>
      <Body className={styles.testList__modal__body__images}>
        <ImagePreviewBlock test={test} getDataUrl={getDataUrl} />
      </Body>
    </Modal>
  );
}

interface Props {
  tests: TestCase[];
  prId: string;
  commitHash: string;
  isStorybookVisualTest?: boolean;
}

// eslint-disable-next-line complexity
function TestList({
  tests,
  prId,
  commitHash,
  isStorybookVisualTest = false,
}: Props) {
  const dispatch = useAppDispatch();
  const [selectedTest, setSelectedTest] = useState<TestCase | null>(null);
  const [containerRef, size] = useContainerSize();

  const handleApprove = (test: TestCase) => {
    dispatch(
      updateTestStatus({
        prId,
        testName: test.name,
        status: 'approved',
        commitHash,
      }),
    );
  };

  /* This functionality would be enabled in next phase */

  // const handleReject = (test: TestCase) => {
  //   dispatch(
  //     updateTestStatus({
  //       prId,
  //       testName: test.name,
  //       status: 'rejected',
  //       commitHash,
  //     }),
  //   );
  // };

  const getDataUrl = (fileName: string | null) =>
    `/frontend/api/visualTests/${prId}/image?pathname=${fileName}`;

  const passedTests = tests.filter((test) => test.status === 'pass').length;

  const [filter, setFilter] = useState<'all' | 'failed'>('all');
  const filteredTests =
    filter === 'failed' ? tests.filter((t) => t.status === 'fail') : tests;

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [filter, filteredTests.length]);

  return (
    <div className={styles.testList}>
      {!isStorybookVisualTest && (
        <div className={styles.testList__header}>
          <div className={styles.testList__header__info}>
            <Text type="h6">{tests[0]?.specFilename || 'Test Cases'}</Text>
            <Text type="sublabel">{tests[0]?.specPath}</Text>
          </div>
          <div className={styles.testList__header__stats}>
            <Text type="sublabel">
              {passedTests}/{tests.length} Passed
            </Text>
          </div>
        </div>
      )}
      {isStorybookVisualTest && (
        <>
          {filteredTests.length > 1 && (
            <div>
              <ProgressBar
                value={currentIndex + 1}
                maxValue={filteredTests.length}
                showText
                variant="info"
              />
            </div>
          )}
          <div className={styles.testList__filters}>
            <Button
              type={filter === 'all' ? 'primary' : 'outlined'}
              size="small"
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button
              type={filter === 'failed' ? 'primary' : 'outlined'}
              size="small"
              onClick={() => setFilter('failed')}
            >
              Failed
            </Button>
          </div>
        </>
      )}
      <div
        ref={containerRef}
        style={{ height: size.height, overflowY: 'auto' }}
        className={styles.testList__items}
        onScroll={
          isStorybookVisualTest && filteredTests.length > 1
            ? (e) => {
                const container = e.target as HTMLDivElement;
                const { scrollTop } = container;
                const totalHeight =
                  container.scrollHeight - container.clientHeight;
                if (totalHeight > 0) {
                  const newIndex = Math.round(
                    (scrollTop / totalHeight) * (filteredTests.length - 1),
                  );
                  setCurrentIndex(newIndex);
                }
              }
            : undefined
        }
      >
        <List data-test-id="test-case-list">
          {isStorybookVisualTest &&
            filteredTests.map((test) => (
              <div
                // eslint-disable-next-line no-underscore-dangle
                key={test._id}
                className={styles.testList__item}
              >
                <div className={styles.testList__item__header}>
                  <div className={styles.testList__item__info}>
                    <Text
                      type="label"
                      bold
                      variant={test.status === 'pass' ? 'default' : 'error'}
                    >
                      {test.name}
                    </Text>
                  </div>

                  <div className={styles.testList__item__middleActions}>
                    <Text
                      type="sublabel"
                      semiBold
                      variant={test.status === 'pass' ? 'success' : 'error'}
                    >
                      {test.status}
                      {test.percentage > 0 &&
                        test.status === 'fail' &&
                        `(${test.percentage}%)`}
                    </Text>

                    <Button
                      type="outlined"
                      size="small"
                      onClick={() => setSelectedTest(test)}
                    >
                      View
                    </Button>
                  </div>

                  <div className={styles.testList__item__actions}>
                    {test.status === 'fail' ? (
                      <>
                        <Button
                          variant="success"
                          size="small"
                          onClick={() => handleApprove(test)}
                        >
                          Approve
                        </Button>
                        {/* This functionality would be enabled in next phase */}
                        {/* <Button
                        size="small"
                        variant="danger"
                        onClick={() => handleReject(test)}
                      >
                        Reject
                      </Button> */}
                      </>
                    ) : (
                      <>
                        {test.approved && (
                          <Text type="sublabel" variant="success">
                            Approved by : {test.approvedBy}
                          </Text>
                        )}

                        {!test.approved && test.baselineApproval && (
                          <>
                            <Text variant="info" type="sublabel">
                              Baseline last updated by :
                              {test.baselineApproval?.approvedBy}
                            </Text>

                            <Text variant="info" type="sublabel">
                              PR: {test.baselineApproval?.approvedFromPr}
                            </Text>
                          </>
                        )}
                        {!test.comparisonPath && !!test.baselinePath && (
                          <Text variant="info" semiBold type="label">
                            New Image, would be added to baseline automatically
                          </Text>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className={styles.testList__item__content}>
                  <div className={styles.testList__item__images}>
                    <ImagePreviewBlock test={test} getDataUrl={getDataUrl} />
                  </div>
                </div>
              </div>
            ))}
        </List>

        {selectedTest && (
          <ImagePreviewModal
            prId={prId}
            test={selectedTest as TestCase}
            onClose={() => {
              setSelectedTest(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default TestList;
